import * as React from 'react';
import 'react-dates/initialize';
import 'moment/locale/ru';
import axios from 'axios';
import AppStore, {Theme} from 'stores/AppStore';
import {observer} from 'mobx-react';
import {RouterProvider} from 'react-router5';
import Router from 'router/Router';
import configureRouter from 'router/configure-router';
import {createGlobalStyle, ThemeProvider} from 'styled-components';
import ImageViewer from "components/modals/ImageViewer";
import ImageViewContext from "context/ImageViewContext";
import {normalize} from 'polished';
import {ToastContainer} from 'react-toastify';
import LightTheme from 'themes/LightTheme';
import DarkTheme from 'themes/DarkTheme';
import {ThemeObject} from 'types';
import 'flickity/css/flickity.css';
import 'react-slidedown/lib/slidedown.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import "react-datepicker/dist/react-datepicker.css";
import 'styles/ReactToastify.css';
import 'styles/Body.css'

axios.interceptors.request.use((config) => {
  config.headers['Accept'] = 'application/json';
  config.headers['Content-Type'] = 'application/json';
  return config;
});

const GlobalStyle = createGlobalStyle`
  ${normalize()}
  
  * {
    outline: none;
    color: inherit;
    font-family: inherit;
    text-decoration: none;
  }
  
  input {
    &:focus::placeholder {
      opacity: 0;
    }
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    
    &[type=number] {
      -moz-appearance: textfield;
    }
  }
  
  button,
  input,
  textarea {
    border: none;
    background: transparent;
  }
  
  button {
    cursor: pointer;
  }
  
  textarea {
    resize: none;
    padding: 0;
    
    &:focus::placeholder {
      opacity: 0;
    }
  }
  
  body {
    -webkit-font-smoothing: subpixel-antialiased;
    -moz-osx-font-smoothing: auto;
    direction: ltr;
    
    color: ${({theme}) => theme.variables.color};
    font-size: 16px;
    font-weight: normal;
    font-family: 'Manrope',
      -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', Geneva, 
      "Noto Sans Armenian", "Noto Sans Bengali", "Noto Sans Cherokee", 
      "Noto Sans Devanagari", "Noto Sans Ethiopic", 
      "Noto Sans Georgian", "Noto Sans Hebrew", "Noto Sans Kannada", 
      "Noto Sans Khmer", "Noto Sans Lao", 
      "Noto Sans Osmanya", "Noto Sans Tamil", "Noto Sans Telugu", 
      "Noto Sans Thai", sans-serif, arial, 
      Tahoma, verdana;
    letter-spacing: 0.01em;
      
    background: ${({theme}) => theme.variables.backgroundColor};
  }

  .dashboard-page .flickity-button {
    max-width: 50px;
    max-height: 50px;
    border: 1px solid ${({theme}) => theme.variables.borderColor};
    background: ${({theme}) => theme.name === Theme.Dark ? '#363636' : '#ffffff'} !important;
    color: #828282 !important;

    transition: opacity .3s;
  }

  .dashboard-page .flickity-button:disabled {
    opacity: 0 !important;
  }

  .dashboard-page .flickity-button.flickity-prev-next-button.previous {
    left: -28px;
  }

  .dashboard-page .flickity-button.flickity-prev-next-button.next {
    right: -22px;
  }
`;

export const router = configureRouter();

/**
 * The provider sets the application settings
 * @constructor
 * @return {React.ReactElement}
 */
function AppContainer(): React.ReactElement {
  const [
    imageViewerData,
    setImageViewerData
  ] = React.useState({
    isOpened: false,
    images: [],
  })

  /**
   * Get the object of the current theme
   * @param {Theme} theme The current theme
   * @return {ThemeObject} The object of current theme
   */
  function getTheme(theme: Theme): ThemeObject {
    switch (theme) {
      case Theme.Light: return LightTheme;
      case Theme.Dark: return DarkTheme;
    }
  }

  return (
    <RouterProvider router={router}>
      <ImageViewContext.Provider value={{imageViewerData, setImageViewerData}}>
        <ThemeProvider theme={getTheme(AppStore.theme)}>
          <GlobalStyle />
          <ToastContainer
            position="top-center"
          />
          <Router />
        </ThemeProvider>
        <ImageViewer />
      </ImageViewContext.Provider>
    </RouterProvider>
  );
}

export default observer(AppContainer);
